import { AnalyticsEventNameType } from "@coeff/api";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { MemoryIcon } from "../../components";
import { useTrack } from "../../utils";

import { decodeBlankLines } from "./markdownBlankLines";
import { RichTextEditor } from "./RichTextEditor";
import { useMemory } from "./useMemory";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  /**
   * Document whose <body> hosts the modal (pass the dashboard iframe's
   * targetDocument). Portaling to the body escapes ancestor stacking contexts —
   * e.g. the chat sidebar (z-300) and navbar — so z-[1000] wins viewport-wide.
   */
  portalDocument?: Document;
}

/**
 * The Memory modal (Figma: AI-Dashboards → Memories). A read view (rendered note)
 * with an Edit mode that swaps in the Lexical RichTextEditor — the same editor
 * used for dashboard text blocks — so the user structures the note with a toolbar
 * (headings, bullets). Full-blob saves, last-write-wins; edits take effect on the
 * agent's next turn (memory is re-read every turn server-side).
 *
 * The editor is UNCONTROLLED: seeded (via `seedKey`) with the saved note when
 * edit mode starts, and its edits tracked through onMarkdownChange.
 *
 * Styled with Tailwind (the viewer tree is portaled into the dashboard iframe
 * where Tailwind's CDN stylesheet is loaded). Click-away is an overlay because
 * cross-document listeners don't fire across the iframe boundary.
 */
export const UserMemoryDrawer: React.FC<Props> = ({ open, setOpen, portalDocument = document }) => {
  const { savedMemory, isLoading, isSaving, maxChars, saveText } = useMemory(open);
  const [isEditing, setIsEditing] = useState(false);
  // The editor's live markdown while editing (seeded from savedMemory on entering edit).
  const [editorMarkdown, setEditorMarkdown] = useState("");
  // Bumping this remounts the Lexical editor to re-seed its initial content.
  const [seedKey, setSeedKey] = useState(0);
  const track = useTrack();

  useEffect(() => {
    if (open) {
      track(AnalyticsEventNameType.WebDashUserMemoryDrawerOpened, {});
    } else {
      setIsEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Empty note → nothing to read, so drop straight into the editor once loaded.
  const showEditor = isEditing || (!isLoading && !savedMemory.trim());

  const beginEdit = () => {
    setEditorMarkdown(savedMemory);
    setSeedKey(k => k + 1);
    setIsEditing(true);
  };

  // Compare decoded (canonical) forms: agent/legacy notes use plain "\n\n" block
  // separators that the editor re-serializes differently without being a real edit.
  const isDirty = decodeBlankLines(editorMarkdown).trim() !== decodeBlankLines(savedMemory).trim();
  const isOverLimit = editorMarkdown.length > maxChars;

  const handleSave = async () => {
    if (isOverLimit || (!isDirty && savedMemory.trim())) {
      return;
    }
    await saveText(editorMarkdown);
    setIsEditing(false);
  };

  if (!open) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/20"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-label="Memory"
        className="relative z-[1000] w-[min(700px,92vw)] bg-main-background text-main-foreground rounded-xl p-4 flex flex-col gap-3 shadow-[0_0_16px_#ebebeb,0_16px_24px_rgba(0,0,0,0.12)]"
      >
        <div className="flex items-center gap-2">
          <MemoryIcon width="20px" height="20px" className="shrink-0 text-main-foreground" />
          <span className="flex-1 text-base font-bold text-main-foreground">Memory</span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            title="Close"
            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-main-background-secondary text-main-muted-foreground hover:text-main-foreground cursor-pointer transition-colors"
          >
            <span className="material-icons leading-none" style={{ fontSize: 14 }}>
              close
            </span>
          </button>
        </div>

        <p className="text-sm text-main-muted-foreground m-0">
          Memory applies across all your dashboards and nothing is saved without your approval.
        </p>

        {isLoading ? (
          <div className="h-[240px] bg-main-background-secondary rounded-lg animate-pulse" />
        ) : showEditor ? (
          // Edit mode: rich editor, pinned toolbar, only the content scrolls. No
          // overflow-hidden here — it clipped the toolbar's heading dropdown (the
          // menu couldn't show its last options); content scrolling is handled
          // inside RichTextEditor via scrollableContent.
          <div className="h-[240px] rounded-lg border border-main-border focus-within:border-primary-button-background">
            <RichTextEditor
              key={`edit-${seedKey}`}
              initialMarkdown={savedMemory}
              onMarkdownChange={setEditorMarkdown}
              placeholder='Nothing saved yet. Ask the AI to "remember" a preference, or write one here.'
              className="h-full"
              scrollableContent
              minHeight="100%"
              maxHeadingLevel={4}
              preserveNewLines
              accentLinks
              showLinkButton={false}
              namespace="UserMemoryEditor"
              contentClassName="text-sm text-main-foreground"
            />
          </div>
        ) : (
          // Read mode: same editor rendered read-only (no toolbar). Per design: the
          // container hugs the content (min height, not a fixed tall box) and the note
          // renders as secondary gray text (headings stay bold via the Lexical theme).
          <div
            className="min-h-[96px] max-h-[240px] overflow-y-auto rounded-lg bg-main-background-secondary"
            data-sentry-mask
          >
            <RichTextEditor
              key={`read-${savedMemory}`}
              initialMarkdown={savedMemory}
              editable={false}
              showToolbar={false}
              preserveNewLines
              accentLinks
              namespace="UserMemoryReader"
              contentClassName="text-sm text-main-muted-foreground"
            />
          </div>
        )}

        {showEditor && isOverLimit && (
          <div className="text-xs text-red-600">
            Over the size limit — shorten the note to save.
          </div>
        )}

        {showEditor ? (
          <div className="flex items-center gap-4 pt-1">
            <span
              className={`flex-1 text-right text-xs ${
                isOverLimit ? "text-red-600" : "text-main-muted-foreground"
              }`}
            >
              {editorMarkdown.length >= maxChars * 0.8
                ? `${editorMarkdown.length} / ${maxChars}`
                : ""}
            </span>
            {savedMemory.trim() && (
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                className="text-sm text-main-muted-foreground hover:text-main-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || isOverLimit || isSaving}
              className="h-8 px-6 text-[14px] font-semibold text-primary-button-foreground bg-primary-button-background hover:bg-primary-button-hover-background hover:text-primary-button-hover-foreground rounded-[4px] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        ) : (
          // Read-mode footer: just Edit, right-aligned (no Clear here — design keeps
          // destructive actions in the edit state).
          <div className="flex items-center justify-end pt-1">
            <button
              type="button"
              onClick={beginEdit}
              className="h-8 px-6 text-[14px] font-semibold text-primary-button-foreground bg-primary-button-background hover:bg-primary-button-hover-background hover:text-primary-button-hover-foreground rounded-[4px] transition-colors"
            >
              Edit
            </button>
          </div>
        )}
      </div>
    </div>,
    portalDocument.body
  );
};
